import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSelectedRestaurant } from './SelectedRestaurantContext';
import { waitersService, ShiftStatus } from '../services/waiters';
import { usersService } from '../services/users';

interface WaiterShiftContextType {
  shiftStatus: ShiftStatus;
  loading: boolean;
  isInTurn: boolean;
  updateShiftStatus: (newStatus: ShiftStatus) => Promise<string>;
  toggleShift: () => Promise<string>;
}

const WaiterShiftContext = createContext<WaiterShiftContextType | undefined>(undefined);

export const useWaiterShift = () => {
  const context = useContext(WaiterShiftContext);
  if (context === undefined) {
    throw new Error('useWaiterShift must be used within a WaiterShiftProvider');
  }
  return context;
};

interface WaiterShiftProviderProps {
  children: ReactNode;
}

export const WaiterShiftProvider: React.FC<WaiterShiftProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { selectedRestaurantId } = useSelectedRestaurant();
  const [shiftStatus, setShiftStatus] = useState<ShiftStatus>('inactive');
  const [loading, setLoading] = useState(false);

  // Función para parsear el status del backend
  const parseBackendStatus = (backendStatus: string | undefined): ShiftStatus => {
    console.log('🔍 Parsing backend status:', backendStatus);
    
    if (!backendStatus || typeof backendStatus !== 'string') {
      console.log('⚠️ Invalid backend status, defaulting to: inactive');
      return 'inactive';
    }
    
    if (backendStatus.startsWith('active_shift:')) {
      console.log('✅ Status parsed as: active');
      return 'active';
    } else if (backendStatus.startsWith('inactive_shift:')) {
      console.log('✅ Status parsed as: inactive');
      return 'inactive';
    }
    console.log('⚠️ Unknown status format, defaulting to: inactive');
    return 'inactive';
  };

  // Función para obtener el status actual del backend
  const fetchCurrentStatus = async () => {
    if (!user?.uid || !selectedRestaurantId) {
      console.log('❌ Missing user.uid or selectedRestaurantId:', { uid: user?.uid, restaurantId: selectedRestaurantId });
      return;
    }
    
    try {
      console.log('🔄 Fetching current shift status for user:', user.uid);
      const userData = await usersService.getUserById(user.uid);
      console.log('📊 User data received:', userData);
      
      if (userData.status) {
        const parsedStatus = parseBackendStatus(userData.status);
        console.log('🎯 Setting shift status to:', parsedStatus);
        setShiftStatus(parsedStatus);
      } else {
        console.log('⚠️ No status found in user data');
      }
    } catch (error) {
      console.error('❌ Error fetching current shift status:', error);
    }
  };

  const updateShiftStatus = async (newStatus: ShiftStatus) => {
    if (!user?.uid || !selectedRestaurantId) {
      throw new Error('Usuario o restaurante no disponible');
    }

    try {
      setLoading(true);
      console.log('🔄 Updating shift status to:', newStatus);
      const response = await waitersService.updateShiftStatus(
        user.uid,
        selectedRestaurantId,
        newStatus
      );
      
      console.log('📊 Backend response:', response);
      
      // Parsear la respuesta del backend
      const parsedStatus = parseBackendStatus(response.status);
      console.log('🎯 Parsed status:', parsedStatus);
      console.log('🔄 Previous state was:', shiftStatus);
      console.log('🔄 New state will be:', parsedStatus);
      
      setShiftStatus(parsedStatus);
      
      return response.status;
    } catch (error) {
      console.error('❌ Error updating shift status:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const toggleShift = async () => {
    const nextStatus: ShiftStatus = shiftStatus === 'active' ? 'inactive' : 'active';
    console.log('🔄 Toggling shift status from', shiftStatus, 'to', nextStatus);
    return await updateShiftStatus(nextStatus);
  };

  // Cargar el status actual cuando cambie el restaurante seleccionado o el usuario
  useEffect(() => {
    console.log('🔄 useEffect triggered:', { selectedRestaurantId, userId: user?.uid });
    if (selectedRestaurantId && user?.uid) {
      fetchCurrentStatus();
    }
  }, [selectedRestaurantId, user?.uid]);

  // Log cuando cambie el estado del turno
  useEffect(() => {
    console.log('🔄 Shift status changed to:', shiftStatus, 'isInTurn:', shiftStatus === 'active');
  }, [shiftStatus]);

  const value: WaiterShiftContextType = {
    shiftStatus,
    loading,
    isInTurn: shiftStatus === 'active',
    updateShiftStatus,
    toggleShift,
  };

  return (
    <WaiterShiftContext.Provider value={value}>
      {children}
    </WaiterShiftContext.Provider>
  );
};
